/**
 * Shopper product comparison state, product-card checkbox sync, and tray.
 * Records keep optional variant data so identity can move to variants later.
 */
(() => {
  // Also read by the compare page when it is opened without ?ids= (the mega
  // menu Compare link). Keep that reader in step with this key and version.
  const STORAGE_KEY = 'meaco-product-compare';
  const STORAGE_VERSION = 1;
  const MAX_PRODUCTS = 3;
  const TTL_MS = 7 * 24 * 60 * 60 * 1000;
  const CHANGE_EVENT = 'product-compare:change';
  const TRAY_VISIBILITY_EVENT = 'product-compare:tray-visibility';
  const CHAT_POLL_INTERVAL = 500;
  const CHAT_POLL_ATTEMPTS = 20;
  const OVERLAY_SELECTOR =
    'filter-drawer[data-open], quick-add-drawer[data-open], quick-cart[data-open]';

  const identity = (item) => String(item?.productId || '');

  const readState = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');

      if (
        parsed?.version !== STORAGE_VERSION ||
        !Array.isArray(parsed.items) ||
        Number(parsed.expiresAt) <= Date.now()
      ) {
        localStorage.removeItem(STORAGE_KEY);
        return [];
      }

      return parsed.items.filter((item) => identity(item) && item.handle).slice(0, MAX_PRODUCTS);
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      return [];
    }
  };

  let items = readState();

  const persist = () => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          version: STORAGE_VERSION,
          expiresAt: Date.now() + TTL_MS,
          items,
        })
      );
    } catch {
      // Keep the in-memory set usable when storage is unavailable.
    }
  };

  const analyticsItem = (item, index) => {
    const entry = {
      item_id: String(item.productId),
      item_name: item.title,
      index,
    };

    if (item.variantId) entry.item_variant = String(item.variantId);

    const price = Number(item.priceAmount);

    if (price) entry.price = price;

    return entry;
  };

  // The custom pixel listens for customer events. A page dataLayer stays on this page.
  const track = (event, item = null) => {
    const publish = window.Shopify?.analytics?.publish;

    if (typeof publish !== 'function') return;

    publish(`meaco:${event}`, {
      compare_count: items.length,
      ecommerce: {
        items: item
          ? [analyticsItem(item, items.findIndex((entry) => identity(entry) === identity(item)))]
          : items.map(analyticsItem),
      },
    });
  };

  const comparePagePath = () => {
    const raw = document.querySelector('product-compare-tray')?.dataset.compareUrl || '/pages/compare';

    try {
      const path = new URL(raw, window.location.origin).pathname.replace(/\/+$/, '');
      return path || '/';
    } catch {
      return '/pages/compare';
    }
  };

  // The tray button builds ?ids= itself. Menu and footer links are plain page
  // URLs, so stamp the saved handles onto those. Links that already name a
  // comparison are left alone.
  const syncCompareLinks = () => {
    const pagePath = comparePagePath();
    const idValue = items
      .map((item) => item.handle)
      .filter(Boolean)
      .join(',');

    for (const link of document.querySelectorAll('a[href]')) {
      if (!(link instanceof HTMLAnchorElement) || link.hasAttribute('data-compare-tray-submit')) {
        continue;
      }

      let url;

      try {
        url = new URL(link.getAttribute('href'), window.location.origin);
      } catch {
        continue;
      }

      const path = url.pathname.replace(/\/+$/, '') || '/';

      if (url.origin !== window.location.origin || path !== pagePath) continue;

      const stamped = link.hasAttribute('data-compare-link');

      if (url.searchParams.has('ids') && !stamped) continue;

      if (idValue) {
        url.searchParams.set('ids', idValue);
        link.setAttribute('data-compare-link', '');
      } else if (stamped) {
        url.searchParams.delete('ids');
        link.removeAttribute('data-compare-link');
      } else {
        continue;
      }

      const next = `${url.pathname}${url.search}${url.hash}`;

      if (link.getAttribute('href') !== next) link.setAttribute('href', next);
    }
  };

  const emit = (detail = {}) => {
    syncCompareLinks();
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, {
        detail: { items: items.map((item) => ({ ...item })), ...detail },
      })
    );
  };

  const store = {
    get max() {
      return MAX_PRODUCTS;
    },

    get items() {
      return items.map((item) => ({ ...item }));
    },

    has(productId) {
      return items.some((item) => identity(item) === String(productId));
    },

    add(item) {
      const key = identity(item);

      if (!key || !item?.handle) return false;

      const existing = items.findIndex((entry) => identity(entry) === key);

      if (existing >= 0) {
        items[existing] = { ...items[existing], ...item };
        persist();
        emit({ action: 'update', item: { ...items[existing] } });
        return true;
      }

      if (items.length >= MAX_PRODUCTS) {
        track('compare_limit', item);
        emit({ action: 'limit', item: { ...item } });
        return false;
      }

      items = [...items, { ...item }];
      persist();
      track('compare_add', item);
      emit({ action: 'add', item: { ...item } });
      return true;
    },

    remove(productId) {
      const removed = items.find((item) => identity(item) === String(productId));

      if (!removed) return false;

      items = items.filter((item) => identity(item) !== String(productId));
      persist();
      track('compare_remove', removed);
      emit({ action: 'remove', item: { ...removed } });
      return true;
    },

    clear() {
      if (items.length === 0) return;

      const previous = items;
      items = [];
      persist();
      track('compare_clear');
      emit({ action: 'clear', previous });
    },

    replace(nextItems, { trackLimit = false } = {}) {
      const valid = [];
      const seen = new Set();

      for (const item of nextItems) {
        const key = identity(item);

        if (!key || !item?.handle || seen.has(key)) continue;
        seen.add(key);
        valid.push({ ...item });
      }

      if (trackLimit && valid.length > MAX_PRODUCTS) track('compare_limit');

      items = valid.slice(0, MAX_PRODUCTS);
      persist();
      emit({ action: 'replace' });
    },

    track,
  };

  window.ProductCompareStore = store;

  const itemFromControl = (input) => ({
    productId: input.dataset.productId || '',
    handle: input.dataset.productHandle || '',
    variantId: input.dataset.productVariantId || null,
    title: input.dataset.productTitle || '',
    url: input.dataset.productUrl || '',
    image: input.dataset.productImage || '',
    price: input.dataset.productPrice || '',
    priceAmount: Number(input.dataset.productPriceAmount) / 100 || null,
    available: true,
  });

  const itemFromCard = (input) => {
    const card = input.closest('[data-product-card]');

    if (!(card instanceof HTMLElement)) return itemFromControl(input);

    const image = card.querySelector(
      '[data-compare-image], .product-card__image--default, .compatible-product-card__image'
    );
    const title = card.querySelector('[data-product-card-title]');
    const price = card.querySelector(
      '[data-product-card-current-price], [data-compare-price-value], [data-compare-price]'
    );
    const selectedSwatch = card.querySelector(
      '[data-product-card-swatch][aria-checked="true"]'
    );

    return {
      productId: card.dataset.productId || '',
      handle: card.dataset.productHandle || '',
      variantId: selectedSwatch?.getAttribute('data-variant-id') || null,
      title: title?.textContent?.trim() || '',
      url: title?.getAttribute('href') || card.dataset.productUrl || '',
      image: image?.currentSrc || image?.getAttribute('src') || '',
      price: price?.textContent?.trim() || '',
      priceAmount:
        Number(selectedSwatch?.getAttribute('data-price-amount') || card.dataset.productPrice) /
          100 || null,
      available: card.dataset.productAvailable === 'true',
    };
  };

  const syncInputs = () => {
    const full = items.length >= MAX_PRODUCTS;

    for (const input of document.querySelectorAll('[data-compare-input]')) {
      if (!(input instanceof HTMLInputElement)) continue;

      const productId = input.getAttribute('data-product-id') || '';
      const checked = store.has(productId);

      input.checked = checked;
      input.disabled = !checked && full;
      input.closest('[data-compare-control]')?.classList.toggle('is-disabled', input.disabled);
    }

    if (new URLSearchParams(window.location.search).get('compare') === '1') {
      for (const form of document.querySelectorAll(
        '.filter-bar__sort, .filter-drawer__form-el'
      )) {
        if (!(form instanceof HTMLFormElement) || form.elements.namedItem('compare')) continue;

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'compare';
        input.value = '1';
        form.append(input);
      }

      for (const link of document.querySelectorAll(
        '[data-filter-remove], [data-filter-clear-all], .filter-drawer__clear'
      )) {
        if (!(link instanceof HTMLAnchorElement)) continue;

        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('compare', '1');
        link.href = `${url.pathname}${url.search}${url.hash}`;
      }
    }
  };

  const onInputChange = (event) => {
    const input = event.target;

    if (!(input instanceof HTMLInputElement) || !input.matches('[data-compare-input]')) return;

    if (input.checked) {
      const item = itemFromCard(input);

      if (!item || !store.add(item)) input.checked = false;
    } else {
      store.remove(input.getAttribute('data-product-id'));
    }
  };

  const onCardVariantChange = (event) => {
    const card = event.target;

    if (!(card instanceof HTMLElement) || !card.matches('[data-product-card]')) return;

    const input = card.querySelector('[data-compare-input]');

    if (!(input instanceof HTMLInputElement) || !input.checked) return;

    const item = itemFromCard(input);

    if (item) store.add(item);
  };

  // The PDP variant payload carries a media id rather than an image URL, so the
  // gallery slide already rendered for that media is the source for the thumbnail.
  const galleryImage = (mediaId) => {
    if (!mediaId) return '';

    const slide = document.querySelector(
      `[data-gallery-slide][data-media-id="${mediaId}"]`
    );
    const image = slide?.querySelector('img');

    return image?.currentSrc || image?.getAttribute('src') || '';
  };

  const onProductVariantChange = (event) => {
    const target = event.target;

    if (!(target instanceof HTMLElement)) return;

    const form = target.closest('[data-product-form]');

    // Accessory cards inside the buy box carry their own compare inputs, so
    // this has to be the PDP control rather than the first match in the form.
    const input = form?.querySelector('[data-product-compare-control] [data-compare-input]');

    if (!(input instanceof HTMLInputElement)) return;

    const variant = event.detail?.variant;
    const price = form.querySelector('[data-current-price-value]');

    if (variant) {
      const image = galleryImage(variant.featured_media_id);

      input.dataset.productVariantId = String(variant.id || '');
      input.dataset.productPriceAmount = String(variant.price || '');

      // Variants without their own media keep the image already on the control.
      if (image) input.dataset.productImage = image;
    }

    input.dataset.productPrice = price?.textContent?.trim() || input.dataset.productPrice || '';

    if (!input.checked) return;

    const item = itemFromControl(input);

    store.add(item);
  };

  document.addEventListener('change', onInputChange);
  document.addEventListener('product-card:variant-change', onCardVariantChange);
  document.addEventListener('variant:change', onProductVariantChange);
  document.addEventListener(CHANGE_EVENT, syncInputs);

  class ProductCompareTray extends HTMLElement {
    #expanded = false;
    #visible = false;
    #overlayWasOpen = false;
    #observer = null;
    #sizeObserver = null;
    #chatPoll = 0;

    connectedCallback() {
      this.addEventListener('click', this.#onClick);
      document.addEventListener(CHANGE_EVENT, this.#onChange);
      this.#observer = new MutationObserver(this.#onMutation);
      this.#observer.observe(document.body, {
        attributes: true,
        subtree: true,
        attributeFilter: ['data-open'],
      });
      this.#sizeObserver = new ResizeObserver(this.#syncPageOffset);
      this.#sizeObserver.observe(this);
      this.#watchChat();
      this.#render();
      syncInputs();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
      document.removeEventListener(CHANGE_EVENT, this.#onChange);
      this.#observer?.disconnect();
      this.#sizeObserver?.disconnect();
      window.clearInterval(this.#chatPoll);
      window.LiveChatWidget?.off?.('visibility_changed', this.#onChatVisibility);
      document.documentElement.classList.remove('has-compare-tray');
      document.documentElement.classList.remove('has-chat-expanded');
      document.documentElement.style.removeProperty('--compare-tray-offset');
      this.#announceVisibility(false);
    }

    #watchChat() {
      const subscribe = () => {
        const widget = window.LiveChatWidget;

        if (typeof widget?.on !== 'function') return false;

        widget.on('visibility_changed', this.#onChatVisibility);

        try {
          this.#onChatVisibility(widget.get('state'));
        } catch {
          /* not ready */
        }

        return true;
      };

      if (subscribe()) return;

      let attempts = 0;

      this.#chatPoll = window.setInterval(() => {
        attempts += 1;

        if (subscribe() || attempts >= CHAT_POLL_ATTEMPTS) {
          window.clearInterval(this.#chatPoll);
          this.#chatPoll = 0;
        }
      }, CHAT_POLL_INTERVAL);
    }

    #onChatVisibility = (state) => {
      document.documentElement.classList.toggle(
        'has-chat-expanded',
        state?.visibility === 'maximized'
      );
    };

    #isSuppressed() {
      return (
        Boolean(document.querySelector(OVERLAY_SELECTOR)) ||
        document.body.hasAttribute('data-compare-table-active')
      );
    }

    #onMutation = () => {
      syncInputs();

      const overlayOpen = Boolean(document.querySelector(OVERLAY_SELECTOR));

      if (this.#overlayWasOpen && !overlayOpen && items.length > 0) {
        this.#expanded = true;
      }

      this.#overlayWasOpen = overlayOpen;
      this.#render();
    };

    #syncPageOffset = () => {
      const visible = !this.hidden;

      document.documentElement.classList.toggle('has-compare-tray', visible);
      this.#announceVisibility(visible);

      if (visible) {
        document.documentElement.style.setProperty(
          '--compare-tray-offset',
          `${this.offsetHeight}px`
        );
      } else {
        document.documentElement.style.removeProperty('--compare-tray-offset');
      }
    };

    /**
     * The sticky ATC occupies the same bottom slot, so it steps aside while the
     * tray is up. Dispatching synchronously keeps both in the same frame.
     */
    #announceVisibility(visible) {
      if (visible === this.#visible) return;

      this.#visible = visible;

      document.dispatchEvent(
        new CustomEvent(TRAY_VISIBILITY_EVENT, { detail: { visible } })
      );
    }

    #onChange = (event) => {
      const action = event.detail?.action;
      const item = event.detail?.item;

      if (action === 'add') {
        this.#expanded = true;
        this.#announce(
          this.dataset.addedTemplate
            ?.replace('__product__', item?.title || '')
            .replace('__count__', String(items.length)) || ''
        );
        requestAnimationFrame(() => {
          this.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      } else if (action === 'remove') {
        this.#announce(
          this.dataset.removedTemplate?.replace('__product__', item?.title || '') || ''
        );
      } else if (action === 'clear') {
        this.#expanded = false;
        this.#announce(this.dataset.clearedLabel || '');
      } else if (action === 'limit') {
        this.#announce(this.dataset.limitLabel || '');
      }

      this.#render();
    };

    #onClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;

      if (!target) return;

      const toggle = target.closest('[data-compare-tray-toggle]');
      const remove = target.closest('[data-compare-tray-remove]');
      const clear = target.closest('[data-compare-tray-clear]');
      const compare = target.closest('[data-compare-tray-submit]');

      if (toggle) {
        this.#expanded = !this.#expanded;
        this.#render();
      } else if (remove) {
        store.remove(remove.getAttribute('data-product-id'));
      } else if (clear) {
        store.clear();
      } else if (compare && items.length >= 2) {
        store.track('compare_begin');
      }
    };

    #announce(message) {
      const live = this.querySelector('[data-compare-live]');

      if (live) live.textContent = message;
    }

    #compareUrl() {
      const url = new URL(this.dataset.compareUrl || '/pages/compare', window.location.origin);
      const view = this.dataset.compareView?.trim();

      if (view) url.searchParams.set('view', view);

      url.searchParams.set('ids', items.map((item) => item.handle).join(','));
      return `${url.pathname}${url.search}`;
    }

    #productElement(item) {
      const product = document.createElement('div');
      const media = document.createElement('span');
      const meta = document.createElement('span');
      const title = document.createElement('span');
      const price = document.createElement('span');
      const remove = document.createElement('button');
      const removeIconTemplate = this.querySelector('[data-compare-tray-remove-icon]');
      const removeIcon =
        removeIconTemplate instanceof HTMLTemplateElement
          ? removeIconTemplate.content.cloneNode(true)
          : document.createDocumentFragment();

      product.className = 'compare-tray__product';
      media.className = 'compare-tray__media';
      meta.className = 'compare-tray__meta';
      title.className = 'compare-tray__title';
      price.className = 'compare-tray__price';
      remove.className = 'compare-tray__remove';
      remove.type = 'button';
      remove.dataset.compareTrayRemove = '';
      remove.dataset.productId = String(item.productId);
      remove.setAttribute(
        'aria-label',
        (this.dataset.removeTemplate || '').replace('__product__', item.title)
      );

      title.textContent = item.title;
      price.textContent = item.price;

      if (item.image) {
        const image = document.createElement('img');
        image.className = 'compare-tray__image';
        image.src = item.image;
        image.alt = '';
        media.append(image);
      }

      meta.append(title, price);
      remove.append(removeIcon);
      product.append(media, meta, remove);
      return product;
    }

    #render() {
      const list = this.querySelector('[data-compare-tray-products]');
      const count = this.querySelectorAll('[data-compare-tray-count]');
      const submit = this.querySelector('[data-compare-tray-submit]');
      const toggle = this.querySelector('[data-compare-tray-toggle]');
      const shouldShow = items.length > 0 && !this.#isSuppressed();

      this.hidden = !shouldShow;
      this.classList.toggle('is-expanded', this.#expanded);
      this.#syncPageOffset();

      for (const node of count) node.textContent = `${items.length}/${MAX_PRODUCTS}`;

      if (toggle) toggle.setAttribute('aria-expanded', this.#expanded ? 'true' : 'false');

      if (list) {
        const products = items.map((item) => this.#productElement(item));
        const slots = Array.from({ length: MAX_PRODUCTS - items.length }, (_, index) => {
          const slot = items.length + index + 1;
          const element = document.createElement('span');

          element.className = 'compare-tray__slot';
          element.setAttribute('aria-hidden', 'true');
          element.textContent = String(slot);
          return element;
        });

        list.replaceChildren(...products, ...slots);
      }

      if (submit instanceof HTMLAnchorElement) {
        const enabled = items.length >= 2;
        const label = enabled
          ? (this.dataset.compareTemplate || '').replace('__count__', `${items.length}/${MAX_PRODUCTS}`)
          : this.dataset.addMoreLabel || '';
        const labelElement = submit.querySelector('.button__label');

        submit.href = enabled ? this.#compareUrl() : '';
        submit.setAttribute('aria-disabled', enabled ? 'false' : 'true');
        submit.tabIndex = enabled ? 0 : -1;

        if (labelElement) {
          labelElement.textContent = label;
        } else {
          submit.textContent = label;
        }
      }
    }
  }

  if (!customElements.get('product-compare-tray')) {
    customElements.define('product-compare-tray', ProductCompareTray);
  }

  const observer = new MutationObserver(syncInputs);
  observer.observe(document.documentElement, { childList: true, subtree: true });

  const start = () => {
    syncInputs();
    syncCompareLinks();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
